import type { ReactNode } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";

import type { DashboardOverview } from "@/api/db/dashboard";
import { DashboardLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

import {
	DashboardCard,
	DashboardCardBody,
	DashboardCardHeader,
	DashboardCardTitle,
} from "./dashboard-card";

const AXIS = { fill: "var(--muted-foreground)", fontFamily: "var(--font-mono)", fontSize: 12 };
const GRID = "var(--border)";

function formattedTooltip(suffix: string) {
	return function TooltipContent({ active, payload, label }: TooltipProps<number, string>) {
		if (!active || !payload || payload.length === 0) {
			return null;
		}

		return (
			<div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-xs shadow-control">
				<p className="font-medium text-foreground">{label}</p>
				<p className="font-mono text-muted-foreground">
					{payload[0].value} {suffix}
				</p>
			</div>
		);
	};
}

function ChartCard({
	component,
	title,
	meta,
	children,
}: {
	component: string;
	title: string;
	meta?: string;
	children: ReactNode;
}) {
	return (
		<DashboardCard data-component={component}>
			<DashboardCardHeader>
				<DashboardCardTitle>{title}</DashboardCardTitle>

				{meta !== undefined && (
					<span className="font-mono text-xs text-muted-foreground">{meta}</span>
				)}
			</DashboardCardHeader>

			<DashboardCardBody>{children}</DashboardCardBody>
		</DashboardCard>
	);
}

function NoData() {
	return (
		<p data-slot="no-data" className="py-6 text-center text-sm text-muted-foreground">
			Sem dados
		</p>
	);
}

function ProportionBar({
	label,
	total,
	max,
	tone,
	muted,
}: {
	label: string;
	total: number;
	max: number;
	tone: string;
	muted?: boolean;
}) {
	return (
		<li data-slot="bar" data-total={total} className="min-w-0">
			<div className="flex items-baseline justify-between gap-2">
				<span
					title={label}
					className={cn("truncate text-sm", muted ? "text-muted-foreground" : "text-foreground")}
				>
					{label}
				</span>

				<span className="shrink-0 font-mono text-xs text-muted-foreground">{total}</span>
			</div>

			<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
				<div
					className={cn("h-full rounded-full", tone)}
					style={{ width: `${(total / max) * 100}%` }}
				/>
			</div>
		</li>
	);
}

function foldItems(items: { value: string; total: number }[], aliases: Record<string, string>) {
	const folded = new Map<string, { label: string; total: number }>();

	for (const item of items) {
		const key = aliases[item.value.trim().toLowerCase()] ?? item.value.trim().toLowerCase();
		const group = folded.get(key);

		if (group) {
			group.total += item.total;
		} else {
			folded.set(key, { label: item.value.trim(), total: item.total });
		}
	}

	return [...folded.entries()]
		.map(([key, group]) => ({ key, label: group.label, total: group.total }))
		.sort((a, b) => b.total - a.total);
}

function topWithOthers(items: { key: string; label: string; total: number }[], top: number) {
	if (items.length <= top + 1) {
		return { head: items, others: 0 };
	}

	const head = items.slice(0, top);
	const others = items.slice(top).reduce((sum, item) => sum + item.total, 0);

	return { head, others };
}

const howFoundAliases: Record<string, string> = {
	outro: "outros",
	"indicação de amigo": "indicação",
};

export function HowFound({ items }: { items: DashboardOverview["how_found"] }) {
	const folded = foldItems(items, howFoundAliases);
	const { head, others } = topWithOthers(folded, 6);
	const max = Math.max(1, ...head.map((item) => item.total));

	return (
		<ChartCard component="dashboard-how-found" title={DashboardLabels.charts.howFound}>
			{head.length === 0 && <NoData />}

			{head.length > 0 && (
				<ol className="flex flex-col gap-2.5">
					{head.map((item) => (
						<ProportionBar
							key={item.key}
							label={item.label}
							total={item.total}
							max={max}
							tone="bg-warning"
						/>
					))}

					{others > 0 && (
						<ProportionBar
							label={DashboardLabels.charts.others}
							total={others}
							max={max}
							tone="bg-warning/40"
							muted
						/>
					)}
				</ol>
			)}
		</ChartCard>
	);
}

export function Insurance({ items }: { items: DashboardOverview["insurance"] }) {
	const mapped = items.map((item) => ({ key: item.value, label: item.value, total: item.total }));
	const { head, others } = topWithOthers(mapped, 5);
	const max = Math.max(1, ...head.map((item) => item.total));

	return (
		<ChartCard component="dashboard-insurance" title={DashboardLabels.charts.insurance}>
			{head.length === 0 && <NoData />}

			{head.length > 0 && (
				<ol className="flex flex-col gap-2.5">
					{head.map((item) => (
						<ProportionBar
							key={item.key}
							label={item.label}
							total={item.total}
							max={max}
							tone="bg-primary"
						/>
					))}

					{others > 0 && (
						<ProportionBar
							label={DashboardLabels.charts.others}
							total={others}
							max={max}
							tone="bg-primary/40"
							muted
						/>
					)}
				</ol>
			)}
		</ChartCard>
	);
}

const podiumChip = [
	"bg-primary text-primary-foreground",
	"bg-info/15 text-info",
	"bg-warning-soft text-warning",
];

export function Referrers({ items }: { items: DashboardOverview["referrers"] }) {
	const ranked = items.slice(0, 8);
	const max = Math.max(1, ...ranked.map((item) => item.total));

	return (
		<ChartCard component="dashboard-referrers" title={DashboardLabels.charts.referrers}>
			{ranked.length === 0 && <NoData />}

			{ranked.length > 0 && (
				<ol className="flex flex-col gap-2.5">
					{ranked.map((item, index) => (
						<li
							key={item.name}
							data-slot="referrer"
							data-total={item.total}
							className="flex min-w-0 items-center gap-2.5"
						>
							<span
								className={cn(
									"grid size-6 shrink-0 place-items-center rounded-full font-mono text-xs font-medium",
									podiumChip[index] ?? "text-muted-foreground",
								)}
							>
								{index + 1}
							</span>

							<div className="min-w-0 flex-1">
								<div className="flex items-baseline justify-between gap-2">
									<span
										title={item.name}
										className={cn("truncate text-sm text-foreground", index < 3 && "font-medium")}
									>
										{item.name}
									</span>

									<span className="shrink-0 font-mono text-xs text-muted-foreground">
										{item.total}
									</span>
								</div>

								<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border">
									<div
										className="h-full rounded-full bg-info"
										style={{ width: `${(item.total / max) * 100}%` }}
									/>
								</div>
							</div>
						</li>
					))}
				</ol>
			)}
		</ChartCard>
	);
}

export function AgeBands({
	bands,
	age,
}: {
	bands: DashboardOverview["age_bands"];
	age: DashboardOverview["age"];
}) {
	const meta =
		age.average === null ? undefined : DashboardLabels.averageAge(age.average, age.min, age.max);

	return (
		<ChartCard component="dashboard-age-bands" title={DashboardLabels.charts.ageBands} meta={meta}>
			<ResponsiveContainer width="100%" height={260}>
				<BarChart data={bands} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
					<CartesianGrid stroke={GRID} vertical={false} />
					<XAxis dataKey="band" tick={AXIS} axisLine={{ stroke: GRID }} tickLine={false} />
					<YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} />
					<Tooltip
						cursor={{ fill: "var(--primary-soft)" }}
						content={formattedTooltip("pacientes")}
					/>
					<Bar dataKey="total" fill="var(--chart-1)" radius={[4, 4, 0, 0]} maxBarSize={48} />
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	);
}

export function Registrations({ months }: { months: DashboardOverview["registrations_by_month"] }) {
	const data = months
		.slice(-12)
		.map((month) => ({ ...month, label: DashboardLabels.month(month.month) }));

	return (
		<ChartCard component="dashboard-registrations" title={DashboardLabels.charts.registrations}>
			{data.length === 0 && <NoData />}

			{data.length > 0 && (
				<ResponsiveContainer width="100%" height={260}>
					<BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
						<CartesianGrid stroke={GRID} vertical={false} />
						<XAxis
							dataKey="label"
							tick={AXIS}
							axisLine={{ stroke: GRID }}
							tickLine={false}
							minTickGap={16}
						/>
						<YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} />
						<Tooltip
							cursor={{ fill: "var(--primary-soft)" }}
							content={formattedTooltip("cadastros")}
						/>
						<Bar dataKey="total" fill="var(--chart-2)" radius={[4, 4, 0, 0]} maxBarSize={28} />
					</BarChart>
				</ResponsiveContainer>
			)}
		</ChartCard>
	);
}
